import { supabase } from '../lib/supabase'
import type {
  SupportCenterCancelReason,
  SupportCenterCancelReasonInput,
  SupportCenterCancelReasonKind,
} from '../types/supportCenterCancelReasons'
import { nowTimestampSaoPaulo } from '../utils/dateOnly'

function sortByName(rows: SupportCenterCancelReason[]): SupportCenterCancelReason[] {
  return [...rows].sort((a, b) =>
    a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
  )
}

function mapRow(row: Record<string, unknown>): SupportCenterCancelReason {
  const kind = row.kind === 'atendimento' ? 'atendimento' : 'triagem'
  return {
    id: String(row.id),
    name: String(row.name ?? ''),
    description: String(row.description ?? ''),
    kind,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    deleted_reason: (row.deleted_reason as string | null) ?? null,
  }
}

export const supportCenterCancelReasonsService = {
  async listByKind(kind: SupportCenterCancelReasonKind): Promise<SupportCenterCancelReason[]> {
    const { data, error } = await supabase
      .from('support_center_cancel_reasons')
      .select('*')
      .eq('kind', kind)
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (error) throw error
    return sortByName((data ?? []).map((row) => mapRow(row as Record<string, unknown>)))
  },

  async upsert(input: SupportCenterCancelReasonInput): Promise<string> {
    const name = input.name.trim()
    const description = input.description.trim()
    if (!name) throw new Error('Informe o nome do motivo.')
    if (!description) throw new Error('Informe a descrição do motivo.')
    if (input.kind !== 'triagem' && input.kind !== 'atendimento') {
      throw new Error('Tipo de motivo inválido.')
    }

    const payload = { name, description, kind: input.kind }

    if (input.id) {
      const { error } = await supabase
        .from('support_center_cancel_reasons')
        .update(payload)
        .eq('id', input.id)
        .is('deleted_at', null)
      if (error) throw error
      return input.id
    }

    const { data, error } = await supabase
      .from('support_center_cancel_reasons')
      .insert(payload)
      .select('id')
      .single()
    if (error) throw error
    return String(data.id)
  },

  async softDelete(id: string, reason: string, userId: string): Promise<void> {
    const deleted_reason = reason.trim()
    if (!deleted_reason) throw new Error('Informe o motivo da exclusão.')

    const { error } = await supabase
      .from('support_center_cancel_reasons')
      .update({
        deleted_at: nowTimestampSaoPaulo(),
        deleted_by: userId,
        deleted_reason,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },
}
